import { useEffect, useMemo, useState } from "react";
import lib from "../assets/utils/lib";

const STORAGE_KEY = "unified_search_catalog";

/** Compact labels for resource chips / selects (full API label stays in `title` + `catalog`). */
export const SEARCH_TARGET_CHIP_LABELS = {
  clients: "Clients",
  enrollees: "Enrollees",
  facilities: "Facilities",
  corporations: "Orgs",
  onboarding: "Onboarding",
  constableItems: "Costables",
  pa: "Pre-auth",
  claims: "Claims",
  checkins: "Encounters",
  appointments: "Appts",
};

function chipDisplayLabel(ui, fallbackLabel) {
  return SEARCH_TARGET_CHIP_LABELS[ui] || fallbackLabel;
}

function catalogRowLabel(row, fallbackLabel) {
  return (
    row?.label ||
    row?.display_label ||
    row?.name ||
    fallbackLabel ||
    row?.resource ||
    ""
  );
}

function catalogRowDenied(row) {
  return row?.allowed_for_user === false || row?.allowed_for_user === 0;
}

function catalogRowNeedsFacility(row, resource) {
  return (
    row?.requires_facility_id === true ||
    row?.requires_facility_id === 1 ||
    row?.rbac_requires_facility_context === true ||
    row?.rbac_requires_facility_context === 1 ||
    resource === "staff" ||
    resource === "tariffs"
  );
}

/**
 * Build picker/chip options from `GET /v2/search/resources` (catalog is source of truth).
 * Falls back to static config only when the catalog is missing or empty.
 * @param {object|null} catalog — normalized `{ resources: [...], ... }`
 * @param {{ label: string, value: string }[]} fallbackOptions — e.g. `searchTarget` from config
 * @param {string} facilityKey — `"none"` or facility id string
 */
export function buildSearchTargetOptions(catalog, fallbackOptions, facilityKey) {
  const fallbackList = Array.isArray(fallbackOptions) ? fallbackOptions : [];
  const fallbackByUi = new Map(fallbackList.map((o) => [o.value, o]));

  const fid =
    facilityKey != null &&
    String(facilityKey).trim() !== "" &&
    String(facilityKey) !== "none"
      ? Number(facilityKey)
      : null;
  const hasFacility = fid != null && !Number.isNaN(fid) && fid > 0;

  const rows = catalog?.resources;
  const hasLiveCatalog = Array.isArray(rows) && rows.length > 0;

  if (!hasLiveCatalog) {
    return fallbackList.map((o) => ({
      ...o,
      label: chipDisplayLabel(o.value, o.label),
      title: o.label,
      disabled: false,
    }));
  }

  const options = [];
  const seenUi = new Set();

  for (const row of rows) {
    const resource = row?.resource ?? row?.key;
    if (!resource) continue;

    const ui = lib.unifiedSearchCatalogResourceToUiTarget(resource);
    if (!ui || seenUi.has(ui)) continue;

    const fallback = fallbackByUi.get(ui) || {
      label: catalogRowLabel(row, ui),
      value: ui,
    };

    seenUi.add(ui);
    const catalogEntry = { ...row, resource };
    const fullLabel = catalogRowLabel(row, fallback.label);
    const label = chipDisplayLabel(ui, fallback.label);

    if (catalogRowDenied(row)) {
      options.push({
        label,
        value: ui,
        catalog: catalogEntry,
        disabled: true,
        title: fullLabel || "You do not have access to this search type.",
      });
      continue;
    }

    if (catalogRowNeedsFacility(row, resource) && !hasFacility) {
      options.push({
        label,
        value: ui,
        catalog: catalogEntry,
        disabled: true,
        title: fullLabel || "Facility context is required for this search type.",
      });
      continue;
    }

    options.push({
      label,
      value: ui,
      catalog: catalogEntry,
      title: fullLabel,
      disabled: false,
    });
  }

  return options;
}

function readCachedCatalog(facilityKey) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.facilityKey === facilityKey && parsed?.data?.resources?.length) {
      return parsed.data;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function normalizeCatalogPayload(body) {
  if (!body || typeof body !== "object") return null;
  const raw =
    body.data != null && typeof body.data === "object" ? body.data : body;
  if (!raw || typeof raw !== "object") return null;
  const resources = Array.isArray(raw.resources)
    ? raw.resources
    : Array.isArray(body.resources)
      ? body.resources
      : Array.isArray(raw.items)
        ? raw.items
        : Array.isArray(raw)
          ? raw
          : [];
  return {
    ...(typeof raw === "object" && !Array.isArray(raw) ? raw : {}),
    catalog_schema_version:
      raw.catalog_schema_version ?? body.catalog_schema_version,
    resources,
  };
}

/**
 * Bootstrap GET /v2/search/resources.
 *
 * @param {{
 *   facilityId?: number|string|null,
 *   enabled?: boolean,
 *   fallbackOptions?: { label: string, value: string }[],
 * }} [options]
 */
export function useUnifiedSearchCatalog({
  facilityId,
  enabled = true,
  fallbackOptions = [],
} = {}) {
  const facilityKey =
    facilityId != null && String(facilityId).trim() !== ""
      ? String(facilityId)
      : "none";

  const [catalog, setCatalog] = useState(() => readCachedCatalog(facilityKey));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCatalog(readCachedCatalog(facilityKey));
  }, [facilityKey]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params =
          facilityKey !== "none" ? { facility_id: Number(facilityKey) } : {};
        const res = await lib.unifiedSearchCatalog(params, ac.signal);
        if (cancelled) return;
        const body = res?.data;
        const ok =
          body &&
          (body.error === 0 || body.error === "0" || Number(body.error) === 0);
        if (!ok) {
          setError(body?.message || "Catalog unavailable");
          setCatalog(null);
          return;
        }
        const normalized = normalizeCatalogPayload(body);
        setCatalog(normalized);
        try {
          sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              facilityKey,
              catalog_schema_version: normalized?.catalog_schema_version,
              data: normalized,
            }),
          );
        } catch {
          /* ignore */
        }
      } catch (e) {
        if (cancelled || ac.signal.aborted) return;
        if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
        setError(e?.message || "Catalog failed");
        setCatalog(readCachedCatalog(facilityKey));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [enabled, facilityKey]);

  const selectOptions = useMemo(
    () => buildSearchTargetOptions(catalog, fallbackOptions, facilityKey),
    [catalog, facilityKey, fallbackOptions],
  );

  return { catalog, loading, error, selectOptions };
}
